"use client";

import SubjectIcon from "@/app/components/logos/SubjectIcon";
import ResourceIcon from "@/app/components/logos/resources/ResourceIcon";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Resources } from "@/types/subject-schema";
import { useMediaQuery } from "@mui/material";
import clsx from "clsx";
import classes from "./animated-background.module.css";

const subjects = ["biology", "chemistry", "physics", "maths aa", "maths ai"];
const resources: Resources[] = [
  "videos",
  "notes",
  "flashcards",
  "questions",
  "definitions",
  "cram sheets",
  "topic tests",
];

/** Deterministic [0, 1) from a string seed so server and client match (avoids hydration error). */
function seeded(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return (Math.abs(h) % 10000) / 10000;
}

export default function AnimatedBackground() {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const subjectCount = isMobile ? 5 : 10;
  const resourceCount = isMobile ? 6 : 12;
  const polygonCount = isMobile ? 16 : 30;

  const duplicatedSubjects = Array.from(
    { length: Math.ceil(subjectCount / subjects.length) },
    () => subjects
  )
    .flat()
    .slice(0, subjectCount);

  const duplicatedResources = Array.from(
    { length: Math.ceil(resourceCount / resources.length) },
    () => resources
  )
    .flat()
    .slice(0, resourceCount);

  return (
    <div className={classes.background}>
      {/* Subject Icons */}
      {duplicatedSubjects.map((subject, i) => {
        const fadeInDelay = seeded(`subject-${i}-delay`);
        return (
          <div
            key={`subject-${i}`}
            className={classes.animatedElement}
            style={
              {
                left: `${seeded(`subject-${i}-left`) * 100}%`,
                top: `${seeded(`subject-${i}-top`) * 100}%`,
                animationDelay: `${fadeInDelay}s, ${fadeInDelay + i * 0.4}s`,
                animationDuration: `18s, ${
                  20 + seeded(`subject-${i}-dur`) * 10
                }s`,
              } as React.CSSProperties
            }
          >
            <SubjectIcon
              subjectColorIndex={getSubjectColorIndex(subject)}
              className={classes.subjectIcon}
            />
          </div>
        );
      })}

      {/* Resource Icons */}
      {duplicatedResources.map((resource, i) => {
        const fadeInDelay = seeded(`resource-${i}-delay`);
        return (
          <div
            key={`resource-${i}`}
            className={classes.animatedElement}
            style={
              {
                left: `${seeded(`resource-${i}-left`) * 100}%`,
                top: `${seeded(`resource-${i}-top`) * 100}%`,
                animationDelay: `${fadeInDelay}s, ${fadeInDelay + i * 0.2}s`,
                animationDuration: `13s, ${
                  20 + seeded(`resource-${i}-dur`) * 10
                }s`,
              } as React.CSSProperties
            }
          >
            <ResourceIcon
              resource={resource}
              size={60}
              backgroundColor="lightblue"
              className={classes.icon}
            />
          </div>
        );
      })}

      {/* Polygons */}
      {Array.from({ length: polygonCount }).map((_, i) => {
        const shapes: ("triangle" | "hexagon" | "square")[] = [
          "triangle",
          "hexagon",
          "square",
        ];
        const shape = shapes[i % shapes.length];
        const fadeInDelay = seeded(`polygon-${i}-delay`);

        return (
          <div
            key={`polygon-${i}`}
            className={clsx(classes.animatedElement, classes.polygon)}
            style={
              {
                left: `${seeded(`polygon-${i}-left`) * 100}%`,
                top: `${seeded(`polygon-${i}-top`) * 100}%`,
                animationDelay: `${fadeInDelay}s, ${fadeInDelay + i * 0.05}s`,
                animationDuration: `10s, ${
                  10 + seeded(`polygon-${i}-dur`) * 15
                }s`,
              } as React.CSSProperties
            }
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              className={classes.polygonSvg}
            >
              {shape === "triangle" && <polygon points="20,5 35,30 5,30" />}
              {shape === "hexagon" && (
                <polygon points="20,5 32,12 32,28 20,35 8,28 8,12" />
              )}
              {shape === "square" && (
                <rect
                  x="10"
                  y="10"
                  width="20"
                  height="20"
                  transform="rotate(45 20 20)"
                />
              )}
            </svg>
          </div>
        );
      })}
    </div>
  );
}
